import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  FileText,
  AlertTriangle,
  CheckCircle,
  Activity,
  TrendingUp,
  ArrowRight,
  Clock,
  Archive,
} from "lucide-react";
import {
  computeCompositeRisk,
  getWorkflowSummary,
  listDocumentHub,
  listAuditEvents,
  listRisks,
  listShipmentCases,
  setComplianceContext,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type {
  AuditEvent,
  CompositeRiskResponse,
  DocumentHubItem,
  ShipmentCaseItem,
  WorkflowSummaryResponse,
} from "../../types/compliance";

export default function ComplianceDashboard() {
  const { credentials } = useComplianceAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Aggregate data
  const [totalRisks, setTotalRisks] = useState(0);
  const [criticalRisks, setCriticalRisks] = useState(0);
  const [recentEvents, setRecentEvents] = useState<AuditEvent[]>([]);
  const [hubDocuments, setHubDocuments] = useState<DocumentHubItem[]>([]);
  const [shipmentCases, setShipmentCases] = useState<ShipmentCaseItem[]>([]);
  const [activeWorkflows, setActiveWorkflows] = useState<
    {
      id: string;
      summary: WorkflowSummaryResponse;
      composite?: CompositeRiskResponse;
    }[]
  >([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [risksData, auditData, hubData, casesData] = await Promise.all([
        listRisks(),
        listAuditEvents(),
        listDocumentHub(),
        listShipmentCases(),
      ]);
      setTotalRisks(risksData.total);
      setCriticalRisks(
        risksData.items.filter((r) => r.severity === "high").length,
      );
      setRecentEvents(auditData.items.slice(0, 10));
      setHubDocuments(hubData.items);
      setShipmentCases(casesData.items);

      // Load workflows from unique workflow IDs in risks
      const workflowIds = [
        ...new Set(risksData.items.map((r) => r.workflow_id)),
      ].slice(0, 5);

      const workflows = await Promise.all(
        workflowIds.map(async (id) => {
          const summary = await getWorkflowSummary(id);
          let composite: CompositeRiskResponse | undefined;
          try {
            composite = await computeCompositeRisk(id);
          } catch {
            // some workflows may have no risks
          }
          return { id, summary, composite };
        }),
      );
      setActiveWorkflows(workflows);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load compliance data",
      );
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, [credentials?.tenantId, credentials?.role]);

  useEffect(() => {
    setComplianceContext(
      credentials?.tenantId ?? "default",
      credentials?.role ?? "COMPLIANCE_ADMIN",
    );
    void loadData();
    pollRef.current = setInterval(() => void loadData(), 60_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadData]);

  const complianceHealth =
    totalRisks === 0
      ? 100
      : Math.round(
          Math.max(
            0,
            100 - criticalRisks * 15 - (totalRisks - criticalRisks) * 5,
          ),
        );

  const batchAnalytics = useMemo(() => {
    const groups = Object.values(
      hubDocuments.reduce<
        Record<string, { batch: DocumentHubItem; documents: DocumentHubItem[] }>
      >((acc, item) => {
        if (!acc[item.batch_id]) acc[item.batch_id] = { batch: item, documents: [] };
        acc[item.batch_id].documents.push(item);
        return acc;
      }, {}),
    ).sort(
      (a, b) =>
        new Date(b.batch.created_at).getTime() -
        new Date(a.batch.created_at).getTime(),
    );

    const completeBatches = groups.filter(({ documents }) => {
      const types = new Set(documents.map((doc) => doc.document_type));
      return ["invoice", "ecmr", "packing_list", "delivery_note", "customs_declaration"].every((type) =>
        types.has(type),
      );
    }).length;

    return {
      groups,
      batchCount: groups.length,
      documentCount: hubDocuments.length,
      completeBatches,
      latest: groups[0],
    };
  }, [hubDocuments]);

  const caseAnalytics = useMemo(() => {
    const openCases = shipmentCases.filter((item) => item.status !== "closed");
    const pendingReviews = shipmentCases.filter(
      (item) => item.status === "ready_for_review" || item.status === "reopened",
    );
    const exceptions = shipmentCases.filter((item) => item.status === "exception");
    const caseIssues = shipmentCases.reduce(
      (sum, item) => sum + item.missing_document_count + item.mismatch_count,
      0,
    );

    return {
      open: openCases.length,
      pendingReviews: pendingReviews.length,
      exceptions: exceptions.length,
      caseIssues,
      latest: shipmentCases[0],
    };
  }, [shipmentCases]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">
          Loading Compliance Workspace…
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg max-w-xl">
          <h3 className="font-bold mb-2">Connection Error</h3>
          <p className="text-sm">{error}</p>
          <p className="text-xs mt-2 text-red-500">
            Make sure the compliance backend is reachable through the LightLink proxy.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="text-blue-600" size={28} />
            Compliance Workspace
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Logistics document compliance, validation, risk & audit center
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-gray-400">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={() => void loadData()}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            ↻ Refresh
          </button>
        </div>
      </div>


      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Active Workflows
            </span>
            <Activity size={16} className="text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {activeWorkflows.length}
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Shipment Cases
            </span>
            <Archive size={16} className="text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {shipmentCases.length}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {caseAnalytics.open} open
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Total Risks
            </span>
            <AlertTriangle size={16} className="text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalRisks}</p>
          {criticalRisks > 0 && (
            <p className="text-xs text-red-600 mt-1">
              {criticalRisks} critical
            </p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Pending Reviews
            </span>
            <Clock size={16} className="text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {caseAnalytics.pendingReviews}
          </p>
          {caseAnalytics.exceptions > 0 && (
            <p className="text-xs text-red-600 mt-1">
              {caseAnalytics.exceptions} exception
            </p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Health Score
            </span>
            <CheckCircle
              size={16}
              className={
                complianceHealth >= 70 ? "text-green-500" : "text-red-500"
              }
            />
          </div>
          <p
            className={`text-2xl font-bold ${
              complianceHealth >= 70 ? "text-green-700" : "text-red-700"
            }`}
          >
            {complianceHealth}%
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <Link
          to="/Compliance/logistics/documents"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition"
        >
          <FileText size={16} />
          Shipment Batch Analysis
          <ArrowRight size={14} />
        </Link>
        <Link
          to="/Compliance/logistics/cases"
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-black text-sm font-medium transition"
        >
          <Archive size={16} />
          Shipment Cases
          <ArrowRight size={14} />
        </Link>
        <Link
          to="/Compliance/logistics/risks"
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium transition"
        >
          <AlertTriangle size={16} />
          View Risks
        </Link>
        <Link
          to="/Compliance/logistics/audit"
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition"
        >
          <Activity size={16} />
          Audit Log
        </Link>
        <Link
          to="/Compliance/logistics/workflows"
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition"
        >
          <TrendingUp size={16} />
          Workflow Center
        </Link>
        <Link
          to="/Compliance/logistics/copilot"
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition"
        >
          <Activity size={16} />
          Compliance Copilot
        </Link>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Archive size={16} className="text-blue-600" />
            Shipment Case Control
          </h2>
          <Link
            to="/Compliance/logistics/cases"
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Open cases -&gt;
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 border-b border-gray-100">
          <div className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Open Cases
            </p>
            <p className="mt-1 text-2xl font-bold text-blue-700">
              {caseAnalytics.open}
            </p>
          </div>
          <div className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Pending Review
            </p>
            <p className="mt-1 text-2xl font-bold text-amber-700">
              {caseAnalytics.pendingReviews}
            </p>
          </div>
          <div className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Exceptions
            </p>
            <p className="mt-1 text-2xl font-bold text-red-700">
              {caseAnalytics.exceptions}
            </p>
          </div>
          <div className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Case Issues
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {caseAnalytics.caseIssues}
            </p>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {shipmentCases.length === 0 ? (
            <p className="p-4 text-sm text-gray-500 text-center">
              No shipment cases yet. Analyze and save a batch to create the first case.
            </p>
          ) : (
            shipmentCases.slice(0, 3).map((item) => (
              <Link
                key={item.case_id}
                to="/Compliance/logistics/cases"
                className="block p-4 hover:bg-gray-50 transition"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-sm font-semibold text-gray-900">
                      {item.case_id}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {item.shipment_reference || item.batch_name}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span>{item.document_count} docs</span>
                    <span>{item.risk_count} risks</span>
                    <span>{item.status.replace(/_/g, " ")}</span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText size={16} className="text-emerald-600" />
            Shipment Batch Analysis
          </h2>
          <Link
            to="/Compliance/logistics/documents"
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Open workspace -&gt;
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 border-b border-gray-100">
          <div className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Saved Batches
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {batchAnalytics.batchCount}
            </p>
          </div>
          <div className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Batch Documents
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {batchAnalytics.documentCount}
            </p>
          </div>
          <div className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Complete Packets
            </p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">
              {batchAnalytics.completeBatches}
            </p>
          </div>
          <div className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Latest Batch
            </p>
            <p className="mt-1 truncate font-mono text-sm font-semibold text-gray-900">
              {batchAnalytics.latest?.batch.batch_id ?? "No batch yet"}
            </p>
            {batchAnalytics.latest && (
              <p className="mt-1 text-xs text-gray-500">
                {batchAnalytics.latest.documents.length} docs -{" "}
                {new Date(batchAnalytics.latest.batch.created_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {batchAnalytics.groups.length === 0 ? (
            <p className="p-4 text-sm text-gray-500 text-center">
              No saved Shipment Batch Analysis records yet.
            </p>
          ) : (
            batchAnalytics.groups.slice(0, 4).map(({ batch, documents }) => (
              <div key={batch.batch_id} className="p-4 hover:bg-gray-50 transition">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {batch.batch_name}
                    </p>
                    <p className="mt-0.5 font-mono text-xs text-gray-500">
                      {batch.batch_id}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span>{documents.length} docs</span>
                    <span>Shipment: {batch.shipment_reference || "Not captured"}</span>
                    <span>{new Date(batch.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Workflows */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp size={16} className="text-blue-600" />
              Active Workflows
            </h2>
            <span className="text-xs text-gray-400">
              {activeWorkflows.length} total
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {activeWorkflows.length === 0 && (
              <p className="p-4 text-sm text-gray-500 text-center">
                No workflows yet. Upload a document to start.
              </p>
            )}
            {activeWorkflows.map((wf) => (
              <div key={wf.id} className="p-4 hover:bg-gray-50 transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 font-mono">
                      {wf.id.slice(0, 16)}…
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {wf.summary.document_count} docs · {wf.summary.risk_count}{" "}
                      risks · {wf.summary.audit_event_count} events
                    </p>
                  </div>
                  <div className="text-right">
                    {wf.composite ? (
                      <>
                        <p
                          className={`text-sm font-bold ${
                            wf.composite.severity === "critical"
                              ? "text-red-600"
                              : wf.composite.severity === "high"
                                ? "text-orange-600"
                                : "text-gray-600"
                          }`}
                        >
                          {(wf.composite.overall_risk_score * 100).toFixed(0)}%
                        </p>
                        <span className="text-[10px] uppercase text-gray-400">
                          {wf.composite.severity}
                        </span>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </div>
                </div>
                {wf.composite && wf.composite.recommendations.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2 line-clamp-1">
                    {wf.composite.recommendations[0]}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recent Audit Events */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Activity size={16} className="text-purple-600" />
              Recent Events
            </h2>
            <Link
              to="/Compliance/logistics/audit"
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              View all →
            </Link>
          </div>
          <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {recentEvents.length === 0 && (
              <p className="p-4 text-sm text-gray-500 text-center">
                No events recorded yet.
              </p>
            )}
            {recentEvents.map((evt) => (
              <div key={evt.event_id} className="p-3 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-gray-800">
                    {evt.event_type}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {new Date(evt.created_at).toLocaleString()}
                  </span>
                </div>
                {evt.actor !== "system" && (
                  <p className="text-xs text-gray-500 mt-0.5">{evt.actor}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
